import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Megaphone, LayoutDashboard, ListTree, Sparkles, Newspaper, Star, BookOpen, Plus, Pencil, Trash2, X, FileText, Award, Calendar, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { useApp, ArticleInput, MustReadInput } from '../store';
import { navItems } from '../data';
const tabs = [{
  id: 'general',
  label: 'Homepage',
  icon: Sparkles
}, {
  id: 'news',
  label: 'News',
  icon: Newspaper
}, {
  id: 'featured',
  label: 'Featured',
  icon: Star
}, {
  id: 'mustreads',
  label: 'Must Reads',
  icon: BookOpen
}, {
  id: 'announcement',
  label: 'Announcement',
  icon: Megaphone
}, {
  id: 'modules',
  label: 'Modules',
  icon: LayoutDashboard
}, {
  id: 'navigation',
  label: 'Navigation',
  icon: ListTree
}] as const;
type TabId = (typeof tabs)[number]['id'];
// Icon choices for Must Read cards
const iconChoices = [{
  key: 'FileText',
  label: 'Document',
  icon: FileText
}, {
  key: 'Award',
  label: 'Award',
  icon: Award
}, {
  key: 'BookOpen',
  label: 'Guide',
  icon: BookOpen
}, {
  key: 'Calendar',
  label: 'Event',
  icon: Calendar
}, {
  key: 'ShieldCheck',
  label: 'Compliance',
  icon: ShieldCheck
}];
function iconToKey(icon: any): string {
  const found = iconChoices.find(c => c.icon === icon);
  return found ? found.key : 'FileText';
}
function keyToIcon(key: string): any {
  const found = iconChoices.find(c => c.key === key);
  return found ? found.icon : FileText;
}
const inputCls = 'w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900';
function Toggle({
  checked,
  onChange,
  label,
  description
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
}) {
  return <div className="flex items-center justify-between py-3.5 border-b border-navy-50 last:border-0" data-id="element-232">
      <div className="pr-4" data-id="element-233">
        <p className="text-sm font-semibold text-navy-900" data-id="element-234">{label}</p>
        <p className="text-xs text-navy-400" data-id="element-235">{description}</p>
      </div>
      <button onClick={onChange} role="switch" aria-checked={checked} aria-label={label} className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-gold-500' : 'bg-navy-200'}`} data-id="element-236">
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} data-id="element-237" />
      </button>
    </div>;
}
function Field({
  label,
  value,
  onChange,
  multiline = false,
  rows = 3,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  return <label className="block" data-id="element-238">
      <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-239">
        {label}
      </span>
      {multiline ? <textarea value={value} rows={rows} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={`${inputCls} resize-none`} data-id="element-240" /> : <input type="text" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-241" />}
    </label>;
}
function SaveRow({
  saved,
  onSave
}: {
  saved: boolean;
  onSave: () => void;
}) {
  return <div className="flex items-center gap-3 pt-2" data-id="element-242">
      <button onClick={onSave} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-243">
        Save changes
      </button>
      {saved && <span className="inline-flex items-center gap-1 text-sm font-semibold text-sage" data-id="element-244">
          <Check className="w-4 h-4" data-id="element-245" /> Saved &amp; published
        </span>}
    </div>;
}
export function AdminPage() {
  const app = useApp();
  const {
    config,
    updateConfig
  } = app;
  const [tab, setTab] = useState<TabId>('general');
  const [saved, setSaved] = useState(false);
  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <>
      <PageHeader title="Site Administration" subtitle="Publish news, feature stories, and configure the intranet for everyone at Independence Home Loans." data-id="element-246" />

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 max-w-4xl" data-id="element-247">
        {/* Tabs */}
        <nav className="flex md:flex-col gap-1 overflow-x-auto" data-id="element-248">
          {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${active ? 'bg-navy-600 text-white' : 'text-navy-600 hover:bg-white'}`} data-id="element-249">
                <Icon className="w-4 h-4" data-id="element-250" /> {t.label}
              </button>;
        })}
        </nav>

        {/* Panel */}
        <motion.div key={tab} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-251">
          {tab === 'general' && <div className="space-y-4" data-id="element-252">
              <div data-id="element-253">
                <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-254">
                  Welcome banner
                </h3>
                <p className="text-sm text-navy-400" data-id="element-255">
                  The greeting shown at the top of the home dashboard.
                </p>
              </div>
              <Field label="Eyebrow label" value={config.welcome.eyebrow} onChange={v => updateConfig({
            welcome: {
              ...config.welcome,
              eyebrow: v
            }
          })} data-id="element-256" />
              <Field label="Heading" value={config.welcome.heading} onChange={v => updateConfig({
            welcome: {
              ...config.welcome,
              heading: v
            }
          })} data-id="element-257" />
              <Field label="Supporting text" multiline value={config.welcome.subtext} onChange={v => updateConfig({
            welcome: {
              ...config.welcome,
              subtext: v
            }
          })} data-id="element-258" />
              <SaveRow saved={saved} onSave={flash} data-id="element-259" />
            </div>}

          {tab === 'news' && <NewsManager data-id="element-260" />}
          {tab === 'featured' && <FeaturedManager data-id="element-261" />}
          {tab === 'mustreads' && <MustReadsManager data-id="element-262" />}

          {tab === 'announcement' && <div className="space-y-4" data-id="element-263">
              <div data-id="element-264">
                <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-265">
                  Site-wide announcement
                </h3>
                <p className="text-sm text-navy-400" data-id="element-266">
                  A banner shown across the top of every page.
                </p>
              </div>
              <Toggle checked={config.announcement.enabled} onChange={() => updateConfig({
            announcement: {
              ...config.announcement,
              enabled: !config.announcement.enabled
            }
          })} label="Show announcement banner" description="Display the banner to all employees." data-id="element-267" />
              <Field label="Message" multiline value={config.announcement.message} onChange={v => updateConfig({
            announcement: {
              ...config.announcement,
              message: v
            }
          })} data-id="element-268" />
              <div data-id="element-269">
                <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-270">
                  Style
                </span>
                <div className="flex gap-2" data-id="element-271">
                  {(['gold', 'navy', 'urgent'] as const).map(tone => <button key={tone} onClick={() => updateConfig({
                announcement: {
                  ...config.announcement,
                  tone
                }
              })} className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-all ${config.announcement.tone === tone ? 'ring-2 ring-gold-400' : 'ring-1 ring-navy-100'} ${tone === 'urgent' ? 'bg-red-600 text-white' : tone === 'gold' ? 'bg-gold-500 text-navy-900' : 'bg-navy-600 text-white'}`} data-id="element-272">
                      {tone === 'urgent' ? 'Emergency' : tone}
                    </button>)}
                </div>
                <p className="text-xs text-navy-400 mt-2" data-id="element-273">
                  Use{' '}
                  <span className="font-semibold text-red-600" data-id="element-274">Emergency</span>{' '}
                  only for urgent, time-sensitive alerts — it shows a red,
                  attention-grabbing banner on every page.
                </p>
              </div>
              <SaveRow saved={saved} onSave={flash} data-id="element-275" />
            </div>}

          {tab === 'modules' && <div data-id="element-276">
              <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-277">
                Homepage modules
              </h3>
              <p className="text-sm text-navy-400 mb-3" data-id="element-278">
                Turn dashboard sections on or off for everyone.
              </p>
              <Toggle checked={config.modules.mustReads} onChange={() => updateConfig({
            modules: {
              ...config.modules,
              mustReads: !config.modules.mustReads
            }
          })} label="Must Reads" description="Company must-read cards on the home dashboard." data-id="element-279" />
              <Toggle checked={config.modules.rateWatch} onChange={() => updateConfig({
            modules: {
              ...config.modules,
              rateWatch: !config.modules.rateWatch
            }
          })} label="Rate Watch widget" description="The mortgage rate watch card in the right rail." data-id="element-280" />
              <Toggle checked={config.modules.featuredAutoplay} onChange={() => updateConfig({
            modules: {
              ...config.modules,
              featuredAutoplay: !config.modules.featuredAutoplay
            }
          })} label="Auto-rotate featured news" description="Cycle the home carousel automatically." data-id="element-281" />
            </div>}

          {tab === 'navigation' && <div data-id="element-282">
              <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-283">
                Sidebar navigation
              </h3>
              <p className="text-sm text-navy-400 mb-3" data-id="element-284">
                Choose which destinations appear in the sidebar. Home is always
                visible.
              </p>
              {navItems.filter(item => item.label !== 'Home').map(item => <NavToggle key={item.label} label={item.label} data-id="element-285" />)}
            </div>}
        </motion.div>
      </div>
    </>;
}
function NavToggle({
  label
}: {
  label: string;
}) {
  const {
    config,
    toggleNavItem
  } = useApp();
  const visible = !config.hiddenNav.includes(label);
  return <Toggle checked={visible} onChange={() => toggleNavItem(label)} label={label} description={visible ? 'Visible in sidebar' : 'Hidden from sidebar'} data-id="element-286" />;
}
/* ------------------------------ News manager ----------------------------- */
const emptyArticle: ArticleInput = {
  category: '',
  title: '',
  excerpt: '',
  author: '',
  authorAvatar: 'https://i.pravatar.cc/120?img=32',
  readTime: '3 min read',
  image: '',
  body: [],
  needsReview: false
};
function NewsManager() {
  const {
    articles,
    addArticle,
    updateArticle,
    removeArticle
  } = useApp();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<ArticleInput & {
    bodyText: string;
  }>({
    ...emptyArticle,
    bodyText: ''
  });
  const startNew = () => {
    setForm({
      ...emptyArticle,
      bodyText: ''
    });
    setEditing('new');
  };
  const startEdit = (id: string) => {
    const a = articles.find(x => x.id === id);
    if (!a) return;
    setForm({
      category: a.category,
      title: a.title,
      excerpt: a.excerpt,
      author: a.author,
      authorAvatar: a.authorAvatar,
      readTime: a.readTime,
      image: a.image,
      body: a.body,
      bodyText: a.body.join('\n\n'),
      needsReview: a.needsReview
    });
    setEditing(id);
  };
  const save = () => {
    const input: ArticleInput = {
      category: form.category || 'Company News',
      title: form.title || 'Untitled',
      excerpt: form.excerpt,
      author: form.author || 'Communications',
      authorAvatar: form.authorAvatar,
      readTime: form.readTime || '3 min read',
      image: form.image,
      body: form.bodyText.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean),
      needsReview: form.needsReview
    };
    if (editing === 'new') addArticle(input);else if (editing) updateArticle(editing, input);
    setEditing(null);
  };
  if (editing) {
    return <div className="space-y-4" data-id="element-287">
        <div className="flex items-center justify-between" data-id="element-288">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-289">
            {editing === 'new' ? 'New article' : 'Edit article'}
          </h3>
          <button onClick={() => setEditing(null)} className="p-1.5 text-navy-400 hover:bg-navy-50 rounded-lg" aria-label="Cancel" data-id="element-290">
            <X className="w-5 h-5" data-id="element-291" />
          </button>
        </div>
        <Field label="Title" value={form.title} onChange={v => setForm(f => ({
        ...f,
        title: v
      }))} data-id="element-292" />
        <div className="grid sm:grid-cols-2 gap-4" data-id="element-293">
          <Field label="Category" value={form.category} placeholder="Company News" onChange={v => setForm(f => ({
          ...f,
          category: v
        }))} data-id="element-294" />
          <Field label="Author" value={form.author} onChange={v => setForm(f => ({
          ...f,
          author: v
        }))} data-id="element-295" />
          <Field label="Read time" value={form.readTime} placeholder="3 min read" onChange={v => setForm(f => ({
          ...f,
          readTime: v
        }))} data-id="element-296" />
          <Field label="Image URL" value={form.image} placeholder="https://…" onChange={v => setForm(f => ({
          ...f,
          image: v
        }))} data-id="element-297" />
        </div>
        <div className="pt-2" data-id="element-298">
          <Toggle checked={!!form.needsReview} onChange={() => setForm(f => ({
          ...f,
          needsReview: !f.needsReview
        }))} label="Flag for review (Stale content)" description="Adds a warning banner that this article may be out of date." data-id="element-299" />
        </div>
        <Field label="Excerpt" multiline rows={2} value={form.excerpt} onChange={v => setForm(f => ({
        ...f,
        excerpt: v
      }))} data-id="element-300" />
        <Field label="Body (separate paragraphs with a blank line)" multiline rows={7} value={form.bodyText} onChange={v => setForm(f => ({
        ...f,
        bodyText: v
      }))} data-id="element-301" />
        <div className="flex gap-3 pt-1" data-id="element-302">
          <button onClick={save} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-303">
            {editing === 'new' ? 'Publish article' : 'Save article'}
          </button>
          <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm font-semibold text-navy-600 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors" data-id="element-304">
            Cancel
          </button>
        </div>
      </div>;
  }
  return <div data-id="element-305">
      <div className="flex items-center justify-between mb-4" data-id="element-306">
        <div data-id="element-307">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-308">News</h3>
          <p className="text-sm text-navy-400" data-id="element-309">
            {articles.length} article{articles.length === 1 ? '' : 's'}{' '}
            published.
          </p>
        </div>
        <button onClick={startNew} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-310">
          <Plus className="w-4 h-4" data-id="element-311" /> New
        </button>
      </div>
      <ul className="divide-y divide-navy-50" data-id="element-312">
        {articles.map(a => <li key={a.id} className="flex items-center gap-3 py-3" data-id="element-313">
            <img src={a.image} alt="" className="w-14 h-10 rounded object-cover shrink-0 bg-navy-50" data-id="element-314" />
            <div className="min-w-0 flex-1" data-id="element-315">
              <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-316">
                {a.title}
              </p>
              <p className="text-xs text-navy-400" data-id="element-317">
                {a.category} · {a.date}
              </p>
            </div>
            <button onClick={() => startEdit(a.id)} className="p-2 text-navy-500 hover:bg-navy-50 rounded-lg" aria-label={`Edit ${a.title}`} data-id="element-318">
              <Pencil className="w-4 h-4" data-id="element-319" />
            </button>
            <button onClick={() => removeArticle(a.id)} className="p-2 text-navy-500 hover:bg-red-50 hover:text-red-600 rounded-lg" aria-label={`Delete ${a.title}`} data-id="element-320">
              <Trash2 className="w-4 h-4" data-id="element-321" />
            </button>
          </li>)}
      </ul>
    </div>;
}
/* ---------------------------- Featured manager --------------------------- */
const accents = ['navy', 'gold', 'sage', 'periwinkle', 'clay'];
function FeaturedManager() {
  const {
    featured,
    articles,
    addFeatured,
    updateFeaturedAccent,
    removeFeatured
  } = useApp();
  const available = articles.filter(a => !featured.some(f => f.id === a.id));
  const [selected, setSelected] = useState('');
  const [accent, setAccent] = useState('navy');
  const add = () => {
    if (!selected) return;
    addFeatured(selected, accent);
    setSelected('');
  };
  return <div data-id="element-322">
      <div className="mb-4" data-id="element-323">
        <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-324">
          Featured stories
        </h3>
        <p className="text-sm text-navy-400" data-id="element-325">
          Promote existing news articles to the home carousel.
        </p>
      </div>

      {/* Add */}
      <div className="bg-navy-50 rounded-xl p-4 mb-5 space-y-3" data-id="element-326">
        <label className="block" data-id="element-327">
          <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-328">
            Feature an article
          </span>
          <select value={selected} onChange={e => setSelected(e.target.value)} className={inputCls} data-id="element-329">
            <option value="" data-id="element-330">Select an article…</option>
            {available.map(a => <option key={a.id} value={a.id} data-id="element-331">
                {a.title}
              </option>)}
          </select>
        </label>
        <div className="flex items-end gap-3" data-id="element-332">
          <div className="flex-1" data-id="element-333">
            <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-334">
              Accent
            </span>
            <div className="flex gap-1.5" data-id="element-335">
              {accents.map(a => <button key={a} onClick={() => setAccent(a)} aria-label={a} className={`w-7 h-7 rounded-full capitalize text-[0] transition-all ${accent === a ? 'ring-2 ring-offset-2 ring-navy-400' : ''} ${a === 'navy' ? 'bg-navy-600' : a === 'gold' ? 'bg-gold-500' : `bg-${a}`}`} style={['sage', 'periwinkle', 'clay'].includes(a) ? {
              backgroundColor: a === 'sage' ? '#57a187' : a === 'periwinkle' ? '#6c7fbe' : '#ac7758'
            } : undefined} data-id="element-336">
                  {a}
                </button>)}
            </div>
          </div>
          <button onClick={add} disabled={!selected} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-337">
            <Plus className="w-4 h-4" data-id="element-338" /> Feature
          </button>
        </div>
        {available.length === 0 && <p className="text-xs text-navy-400" data-id="element-339">
            All articles are already featured. Add more in the News tab.
          </p>}
      </div>

      {/* List */}
      <ul className="divide-y divide-navy-50" data-id="element-340">
        {featured.map(f => <li key={f.id} className="flex items-center gap-3 py-3" data-id="element-341">
            <img src={f.image} alt="" className="w-14 h-10 rounded object-cover shrink-0 bg-navy-50" data-id="element-342" />
            <div className="min-w-0 flex-1" data-id="element-343">
              <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-344">
                {f.title}
              </p>
              <p className="text-xs text-navy-400" data-id="element-345">{f.category}</p>
            </div>
            <select value={f.accent} onChange={e => updateFeaturedAccent(f.id, e.target.value)} className="text-xs rounded-lg bg-navy-50 border border-transparent px-2 py-1.5 text-navy-700 capitalize focus:outline-none focus:ring-2 focus:ring-gold-400/30" aria-label="Accent color" data-id="element-346">
              {accents.map(a => <option key={a} value={a} data-id="element-347">
                  {a}
                </option>)}
            </select>
            <button onClick={() => removeFeatured(f.id)} className="p-2 text-navy-500 hover:bg-red-50 hover:text-red-600 rounded-lg" aria-label={`Remove ${f.title} from featured`} data-id="element-348">
              <Trash2 className="w-4 h-4" data-id="element-349" />
            </button>
          </li>)}
        {featured.length === 0 && <li className="py-6 text-center text-sm text-navy-400" data-id="element-350">
            No featured stories yet.
          </li>}
      </ul>
    </div>;
}
/* --------------------------- Must Reads manager -------------------------- */
function MustReadsManager() {
  const {
    mustReads,
    addMustRead,
    updateMustRead,
    removeMustRead
  } = useApp();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState({
    title: '',
    category: '',
    due: '',
    iconKey: 'FileText'
  });
  const startNew = () => {
    setForm({
      title: '',
      category: '',
      due: '',
      iconKey: 'FileText'
    });
    setEditing('new');
  };
  const startEdit = (id: string) => {
    const m = mustReads.find(x => x.id === id);
    if (!m) return;
    setForm({
      title: m.title,
      category: m.category,
      due: m.due,
      iconKey: iconToKey(m.icon)
    });
    setEditing(id);
  };
  const save = () => {
    const input: MustReadInput = {
      title: form.title || 'Untitled',
      category: form.category || 'General',
      due: form.due || 'Read soon',
      icon: keyToIcon(form.iconKey)
    };
    if (editing === 'new') addMustRead(input);else if (editing) updateMustRead(editing, input);
    setEditing(null);
  };
  if (editing) {
    return <div className="space-y-4" data-id="element-351">
        <div className="flex items-center justify-between" data-id="element-352">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-353">
            {editing === 'new' ? 'New must-read' : 'Edit must-read'}
          </h3>
          <button onClick={() => setEditing(null)} className="p-1.5 text-navy-400 hover:bg-navy-50 rounded-lg" aria-label="Cancel" data-id="element-354">
            <X className="w-5 h-5" data-id="element-355" />
          </button>
        </div>
        <Field label="Title" value={form.title} onChange={v => setForm(f => ({
        ...f,
        title: v
      }))} data-id="element-356" />
        <div className="grid sm:grid-cols-2 gap-4" data-id="element-357">
          <Field label="Category" value={form.category} placeholder="Compliance" onChange={v => setForm(f => ({
          ...f,
          category: v
        }))} data-id="element-358" />
          <Field label="Call to action" value={form.due} placeholder="Acknowledge by Fri" onChange={v => setForm(f => ({
          ...f,
          due: v
        }))} data-id="element-359" />
        </div>
        <div data-id="element-360">
          <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-361">
            Icon
          </span>
          <div className="flex flex-wrap gap-2" data-id="element-362">
            {iconChoices.map(c => {
            const Icon = c.icon;
            const active = form.iconKey === c.key;
            return <button key={c.key} onClick={() => setForm(f => ({
              ...f,
              iconKey: c.key
            }))} aria-label={c.label} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${active ? 'bg-gold-500 text-navy-900 ring-2 ring-gold-400' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`} data-id="element-363">
                  <Icon className="w-5 h-5" data-id="element-364" />
                </button>;
          })}
          </div>
        </div>
        <div className="flex gap-3 pt-1" data-id="element-365">
          <button onClick={save} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-366">
            {editing === 'new' ? 'Add must-read' : 'Save'}
          </button>
          <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm font-semibold text-navy-600 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors" data-id="element-367">
            Cancel
          </button>
        </div>
      </div>;
  }
  return <div data-id="element-368">
      <div className="flex items-center justify-between mb-4" data-id="element-369">
        <div data-id="element-370">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-371">
            Must Reads
          </h3>
          <p className="text-sm text-navy-400" data-id="element-372">
            Cards shown on the home dashboard.
          </p>
        </div>
        <button onClick={startNew} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-373">
          <Plus className="w-4 h-4" data-id="element-374" /> New
        </button>
      </div>
      <ul className="divide-y divide-navy-50" data-id="element-375">
        {mustReads.map(m => {
        const Icon = m.icon;
        return <li key={m.id} className="flex items-center gap-3 py-3" data-id="element-376">
              <span className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center text-navy-600 shrink-0" data-id="element-377">
                {Icon ? <Icon className="w-4 h-4" data-id="element-378" /> : null}
              </span>
              <div className="min-w-0 flex-1" data-id="element-379">
                <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-380">
                  {m.title}
                </p>
                <p className="text-xs text-navy-400" data-id="element-381">
                  {m.category} · {m.due}
                </p>
              </div>
              <button onClick={() => startEdit(m.id)} className="p-2 text-navy-500 hover:bg-navy-50 rounded-lg" aria-label={`Edit ${m.title}`} data-id="element-382">
                <Pencil className="w-4 h-4" data-id="element-383" />
              </button>
              <button onClick={() => removeMustRead(m.id)} className="p-2 text-navy-500 hover:bg-red-50 hover:text-red-600 rounded-lg" aria-label={`Delete ${m.title}`} data-id="element-384">
                <Trash2 className="w-4 h-4" data-id="element-385" />
              </button>
            </li>;
      })}
        {mustReads.length === 0 && <li className="py-6 text-center text-sm text-navy-400" data-id="element-386">
            No must-reads yet.
          </li>}
      </ul>
    </div>;
}